import { useEffect, useState } from "react"
import type { ProdutoCatalogo } from "@/api"
import { Box, Collapse, Dialog, DialogTitle, Fade, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material"
import { capitalize } from "@/lib/formato"
import { passoIndisponivel, type EtapaPasso, type PassoSelecionado, type SelecoesPassos } from "../passos"
import { AvisoPassoIndisponivel } from "@/components/aviso-passo-indisponivel"
import { useStepWizard } from "./use-step-wizard"
import { useAcompanharPasso } from "./use-acompanhar-passo"
import { apoioDaEtapa, avisoAdicionaisRemovidos, numerosDasEtapas, passoObrigatorio, textoRegraPasso, tituloDaEtapa, type PassoStatus } from "./passo-utils"
import StepProgress from "./step-progress"
import StepAccordionItem from "./step-accordion-item"
import StepOptionCard from "./step-option-card"
import StepSummary from "./step-summary"
import StepFooter from "./step-footer"
import CloseIcon from '@mui/icons-material/Close'

export interface ItemPersonalizado {
    selecoes: SelecoesPassos
    observacao: string
}

interface DialogPassosProps {
    /** Produto a personalizar; `null` mantém o diálogo fechado. */
    produto: ProdutoCatalogo | null
    /** Passos do item em edição — `null` num item novo. */
    passosSalvos?: PassoSelecionado[] | null
    observacaoInicial?: string
    /** Campo de observação no resumo (modos onde o cliente tem teclado). */
    permitirObservacao?: boolean
    /** Largura do diálogo: 'md' aproveita a tela do tablet; o totem de checkout segue em 'sm'. */
    largura?: 'sm' | 'md'
    /** Adicional por sabor: fora no totem checkout, cuja `/adicionar-itens` só aceita quantidade inteira. */
    permitirAdicionalPorSabor?: boolean
    onFechar: () => void
    onConfirmar: (item: ItemPersonalizado) => void
}

function DialogPassos({
    produto,
    passosSalvos = null,
    observacaoInicial = '',
    permitirObservacao = false,
    largura = 'sm',
    permitirAdicionalPorSabor = false,
    onFechar,
    onConfirmar,
}: DialogPassosProps): JSX.Element {
    const theme = useTheme()
    // As duas consultas SEMPRE são avaliadas: `a() || b()` curto-circuita e o
    // segundo useMediaQuery viraria hook condicional — a ordem dos hooks muda
    // quando a largura cruza 600px e o React derruba a tela inteira.
    const telaEstreita = useMediaQuery(theme.breakpoints.down('sm'))
    const telaBaixa = useMediaQuery('(max-height: 720px)')
    // Tela baixa (tablet Android deitado): diálogo com margem sobra pouco espaço
    // útil — melhor ocupar tudo, como um app faria.
    const fullScreen = telaEstreita || telaBaixa

    const aberto = Boolean(produto)
    const editando = Boolean(passosSalvos)

    const wizard = useStepWizard({ produto, passosSalvos, aberto, adicionalPorOpcao: permitirAdicionalPorSabor })
    const { etapas, etapaAtual, indiceAtivo, selecoes, adicionaisRemovidos } = wizard
    const numeros = numerosDasEtapas(etapas)
    const listaRef = useAcompanharPasso(indiceAtivo, aberto && !wizard.concluido)

    // A miniatura só entra num passo em que alguma opção tem foto: quase nenhuma loja
    // cadastra imagem em adicional, e uma foto solta no meio da lista desalinha o resto.
    const passosComImagem = new Set(
        etapas.filter(({ passo }) => (passo.tabelaPreco?.itens ?? []).some((o) => !!o.imagem)).map((e) => e.chave),
    )

    const [observacao, setObservacao] = useState(observacaoInicial)

    // Reabrir o diálogo (produto novo ou outro item em edição) recomeça limpo.
    useEffect(() => {
        if (aberto) setObservacao(observacaoInicial)
    }, [aberto, produto, observacaoInicial])

    const statusDoPasso = (etapa: EtapaPasso, indice: number): PassoStatus => {
        if (indice === indiceAtivo) return 'ativo'
        if (!wizard.podeAcessar(indice)) return 'bloqueado'
        if (wizard.passoValido(etapa) && wizard.quantidadePasso(etapa.chave) > 0) return 'concluido'
        return 'pendente'
    }

    const handleConfirm = (): void => {
        if (!produto || !wizard.todosValidos) return

        onConfirmar({ selecoes, observacao: observacao.trim() })
    }

    const ehUltimo = indiceAtivo === etapas.length - 1
    const proximoOpcional = etapaAtual ? !passoObrigatorio(etapaAtual.passo) && wizard.quantidadePasso(etapaAtual.chave) === 0 : false
    const pularLabel = etapaAtual?.pai ? 'Sem adicionais' : 'Pular passo'
    const proximoLabel = proximoOpcional ? pularLabel : ehUltimo ? 'Revisar' : 'Próximo'

    // O contador conta só os passos: nos adicionais de um sabor ele fica no passo pai, sem o total crescer a cada sabor.
    const passosAteAqui = etapas.slice(0, indiceAtivo + 1).filter((etapa) => !etapa.pai)
    const passoDoContador = passosAteAqui[passosAteAqui.length - 1]
    const totalPassos = etapas.filter((etapa) => !etapa.pai).length
    const progressoAtual = passoDoContador && passoDoContador.passo.quantidadeMaxima > 0
        ? Math.min(wizard.quantidadePasso(passoDoContador.chave) / passoDoContador.passo.quantidadeMaxima, 1)
        : 0

    return (
        <Dialog
            open={aberto}
            onClose={onFechar}
            maxWidth={largura}
            fullWidth
            fullScreen={fullScreen}
            PaperProps={{
                sx: {
                    // Em tela cheia o sx não pode reintroduzir canto e altura de
                    // diálogo: o sx vence a classe do MUI e sobravam faixas do
                    // backdrop em cima e embaixo.
                    borderRadius: fullScreen ? 0 : 4,
                    height: fullScreen ? '100%' : { sm: '92vh' },
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <DialogTitle component="div" sx={{ pr: 7, pb: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {editando ? 'Editar item' : 'Personalize seu item'}
                </Typography>

                <Typography variant="body1" color="text.secondary">
                    {capitalize(produto?.descricao ?? '')}
                </Typography>

                <IconButton onClick={onFechar} aria-label="Fechar" sx={{ position: 'absolute', top: 12, right: 12 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {etapas.length === 0 ? (
                <Box sx={{ p: 4 }}>
                    <Typography color="text.secondary">Este produto não possui personalização de itens.</Typography>
                </Box>
            ) : (
                <>
                    <Collapse in={!wizard.concluido} timeout={300}>
                        <Box sx={{ px: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <StepProgress
                                indiceAtivo={Math.max(passosAteAqui.length - 1, 0)}
                                total={totalPassos}
                                progressoAtual={wizard.concluido ? 1 : progressoAtual}
                                detalhe={etapaAtual?.pai ? 'adicionais' : undefined}
                            />
                        </Box>
                    </Collapse>

                    <Box sx={{ position: 'relative', flexGrow: 1, overflow: 'hidden', backgroundColor: 'background.default' }}>
                        <Fade in={!wizard.concluido} timeout={300} unmountOnExit>
                            <Stack
                                ref={listaRef}
                                spacing={1.5}
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    overflowY: 'auto',
                                    overscrollBehavior: 'contain',
                                    p: 2,
                                    // Sem isto os passos ENCOLHEM: numa coluna flex de altura
                                    // fixa o filho tem flex-shrink 1, e como cada passo tem
                                    // overflow hidden, o conteúdo é cortado em vez de esticar.
                                    // O container fica do tamanho da tela, scrollHeight igual a
                                    // clientHeight, e não há o que rolar — as opções de baixo
                                    // ficam inalcançáveis.
                                    '& > *': { flexShrink: 0 },
                                }}
                            >
                                {etapas.map((etapa, indice) => {
                                    const { chave, passo, pai } = etapa

                                    return (
                                        <StepAccordionItem
                                            key={chave}
                                            ordem={pai ? '+' : numeros[indice] ?? indice + 1}
                                            titulo={tituloDaEtapa(etapa, numeros[indice] ?? indice + 1)}
                                            regra={passoIndisponivel(passo) ? 'Sem opções disponíveis' : textoRegraPasso(passo)}
                                            apoio={pai ? apoioDaEtapa(etapa) : undefined}
                                            status={statusDoPasso(etapa, indice)}
                                            selecionados={wizard.quantidadePasso(chave)}
                                            onClick={() => wizard.irPara(indice)}
                                        >
                                            {/* Passo que chegou sem opção porque a loja pausou todas: o wizard já
                                                trava aqui pelo mínimo não atingido, e sem o aviso o cliente encara
                                                um passo vazio. Totem e tablet são da loja, então o texto manda
                                                chamar o atendente em vez de oferecer WhatsApp. */}
                                            {passoIndisponivel(passo) && (
                                                <AvisoPassoIndisponivel nomeProduto={capitalize(produto?.descricao ?? '')} passo={passo} />
                                            )}

                                            {(passo.tabelaPreco?.itens ?? []).map((opcao) => (
                                                <StepOptionCard
                                                    key={opcao.id}
                                                    nome={opcao.nomeProduto}
                                                    descricao={opcao.descricao}
                                                    imagem={opcao.imagem}
                                                    mostrarImagem={passosComImagem.has(chave)}
                                                    // No adicional do sabor o card já mostra o que será cobrado: a fração do preço cheio.
                                                    valor={opcao.valor * (pai?.fracao ?? 1)}
                                                    quantidade={selecoes[chave]?.[opcao.id] ?? 0}
                                                    maxAtingido={wizard.quantidadePasso(chave) >= passo.quantidadeMaxima}
                                                    onAdd={() => wizard.alterarOpcao(etapa, opcao.id, 1)}
                                                    onRemove={() => wizard.alterarOpcao(etapa, opcao.id, -1)}
                                                />
                                            ))}

                                            {adicionaisRemovidos?.chave === chave && (
                                                <Typography role="status" variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                                                    {avisoAdicionaisRemovidos(adicionaisRemovidos.adicionais, adicionaisRemovidos.sabor)}
                                                </Typography>
                                            )}
                                        </StepAccordionItem>
                                    )
                                })}
                            </Stack>
                        </Fade>

                        <Fade in={wizard.concluido} timeout={300} unmountOnExit>
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
                                <StepSummary
                                    etapas={etapas}
                                    selecoes={selecoes}
                                    onEditar={wizard.irPara}
                                    observacao={observacao}
                                    onObservacaoChange={permitirObservacao ? setObservacao : undefined}
                                />
                            </Box>
                        </Fade>
                    </Box>

                    <StepFooter
                        total={wizard.finalPrice}
                        editando={editando}
                        concluido={wizard.concluido}
                        podeVoltar={indiceAtivo > 0}
                        proximoLabel={proximoLabel}
                        proximoHabilitado={etapaAtual ? wizard.passoValido(etapaAtual) : false}
                        confirmarHabilitado={wizard.todosValidos}
                        onVoltar={wizard.voltar}
                        onAvancar={wizard.avancar}
                        onConfirmar={handleConfirm}
                    />
                </>
            )}
        </Dialog>
    )
}

export default DialogPassos
